import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

/**
 * Public privacy policy at `/privacy` — no sign-in required (store listings,
 * the sign-up screen and the mobile Settings link all point here).
 *
 * Every statement below is meant to describe what the system ACTUALLY does
 * today, not what it should do: when a data flow changes (push ships, Sentry
 * ships, location handling is fixed — BACKLOG GAP-40/51/58, export ships —
 * REQ-111), this page changes in the same PR. Bump LAST_UPDATED when it does.
 *
 * Beta draft: not yet reviewed by a lawyer (LAUNCH_CHECKLIST.md §2).
 */

const LAST_UPDATED = 'October 1, 2026';
const PRIVACY_EMAIL = 'privacy@wayplume.com';
const MIN_AGE = 16;

const PrivacyPolicy: React.FC = () => (
  <div className="privacy-page">
    <header className="privacy-page__nav">
      <Link className="privacy-page__brand" to="/">
        <span className="privacy-page__mark">W</span>
        <span>Wayplume</span>
      </Link>
    </header>

    <main className="privacy-page__body">
      <h1>Privacy Policy</h1>
      <p className="privacy-page__meta">
        Last updated: {LAST_UPDATED} · Applies to the Wayplume website and the Wayplume apps for iOS and
        Android, which are currently in beta.
      </p>

      <p>
        Wayplume is a social trip-planning app with an AI planner. This policy explains what personal
        information we collect, why, who we share it with, how long we keep it and the choices you have.
        We do not sell your personal information, we do not show ads, and we do not use third-party
        advertising or analytics trackers.
      </p>

      <h2>1. Information we collect</h2>
      <h3>You give us</h3>
      <ul>
        <li>
          <strong>Account details:</strong> email address and password (handled by our sign-in provider —
          we never see your password), first and last name, and nickname. If you sign in with Google, we
          receive your name and email from Google.
        </li>
        <li>
          <strong>Optional profile details:</strong> profile photo, birthdate, phone number, address,
          language and time zone — only if you add them.
        </li>
        <li>
          <strong>Trips:</strong> destinations, dates, budgets, party size, itineraries, activities,
          checklists and notes.
        </li>
        <li>
          <strong>Social content:</strong> posts, photos, stories, comments, likes, shares, follows, friend
          connections, direct messages, blocks and reports.
        </li>
        <li>
          <strong>Conversations with the AI planner:</strong> the messages you send and the replies you get.
        </li>
      </ul>

      <h3>Collected when you use the app</h3>
      <ul>
        <li>
          <strong>Location (only if you allow it on your device):</strong> used to centre the map and suggest
          places near you. While the permission is on, your device's precise location is sent with each
          message to the AI planner and stored with that conversation. See section 4 for retention and
          section 7 for how to turn it off.
        </li>
        <li>
          <strong>Photos you upload:</strong> we remove all embedded metadata, including GPS coordinates,
          before a photo is stored or shown to anyone.
        </li>
        <li>
          <strong>Technical data:</strong> server logs (such as request times, error details and
          identifiers needed to operate the service) and, on the web, data your browser stores locally to keep
          you signed in and remember your settings.
        </li>
        <li>
          <strong>Calendar (only if you choose to export):</strong> the app writes your trip into your
          device calendar. It does not read your existing events.
        </li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To provide the service: your account, trips, maps, calendar export, feed and messaging.</li>
        <li>
          To run the AI planner: suggest places, build and edit itineraries and remember your travel
          preferences between conversations.
        </li>
        <li>To show your content to the people you choose, based on your account and trip visibility settings.</li>
        <li>
          To keep the community safe: we automatically check uploaded photos for unsafe content before they
          become visible, and we review reports.
        </li>
        <li>To secure the service, prevent abuse, fix problems and comply with the law.</li>
        <li>To contact you about your account (for example verification codes and password resets).</li>
      </ul>

      <h2>3. Who we share it with</h2>
      <p>
        <strong>Other users</strong> see what you choose to share: your public profile (name, nickname,
        photo) and the trips and posts whose visibility allows them. Your email, birthdate, phone number and
        address are never shown to other users.
      </p>
      <p>
        <strong>Service providers</strong> process data on our behalf to run Wayplume:
      </p>
      <ul>
        <li>
          <strong>Amazon Web Services</strong> — hosting, databases, file storage, sign-in (Amazon Cognito),
          photo safety checks (Amazon Rekognition) and the AI planner (Amazon Bedrock, using Anthropic Claude
          models). Your conversations are processed to generate replies; they are not used to train those
          models.
        </li>
        <li>
          <strong>Google</strong> — maps, place search and place details (Google Maps Platform), and Google
          sign-in if you use it.
        </li>
        <li>
          <strong>Tavily</strong> — web search the AI planner uses for destination information. Search queries
          are written by the AI from your conversation.
        </li>
        <li>
          <strong>Apple and Google</strong> — app distribution through their app stores.
        </li>
      </ul>
      <p>
        We may also disclose information if required by law, to protect the safety of people or the
        service, or as part of a merger or sale of the business (in which case this policy continues to
        apply).
      </p>

      <h2>4. How long we keep it</h2>
      <ul>
        <li>Account, profile, trips, posts and messages: until you delete them or delete your account.</li>
        <li>AI planner conversations (including any location sent with them): 180 days.</li>
        <li>What the AI planner remembers about your preferences: 30 days.</li>
        <li>Stories: 2 days.</li>
        <li>Server logs: up to 90 days.</li>
        <li>
          Database backups: deleted information may remain in encrypted backups for up to 35 days before it
          is overwritten.
        </li>
      </ul>

      <h2>5. Where your information is stored</h2>
      <p>
        Wayplume's servers are located in the United States (Oregon). If you live elsewhere, including in
        Canada, your information is transferred to and processed in the United States, where it may be
        accessible to authorities under US law.
      </p>

      <h2>6. How we protect it</h2>
      <p>
        Data is encrypted in transit (HTTPS) and at rest, access to production systems is restricted, and
        sign-in tokens on mobile are stored in the device's secure storage. No system is perfectly secure;
        if a breach affects your information in a way that creates a real risk of significant harm, we will
        notify you and the relevant privacy regulator as the law requires.
      </p>

      <h2>7. Your choices and rights</h2>
      <ul>
        <li>
          <strong>Correct</strong> your profile at any time in Settings.
        </li>
        <li>
          <strong>Delete your account</strong> in Settings → Delete account (web and app). This permanently
          removes your profile, trips, posts, comments, likes, photos, connections, direct messages, AI
          conversations and notifications. Messages you sent in group conversations are replaced with a
          "deleted" placeholder. Trips that other people already copied remain theirs.
        </li>
        <li>
          <strong>Access or get a copy</strong> of your information: email us at{' '}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. We answer within 30 days.
        </li>
        <li>
          <strong>Location:</strong> turn off location access for Wayplume in your device or browser
          settings. The app keeps working; the map and suggestions just won't use your position.
        </li>
        <li>
          <strong>Visibility:</strong> choose who sees your account and each trip in Settings and on the trip
          itself. You can block other users at any time.
        </li>
        <li>
          <strong>Withdraw consent</strong> to any optional processing by turning the feature off or
          contacting us.
        </li>
      </ul>
      <p>
        If you're not satisfied with our answer, you can complain to the Office of the Privacy Commissioner of
        Canada, or, if you live in Quebec, the Commission d'accès à l'information, or to your local privacy
        authority.
      </p>

      <h2>8. Children</h2>
      <p>
        Wayplume is not intended for anyone under {MIN_AGE}. We do not knowingly collect information from
        children under that age; if you believe a child has created an account, contact us and we will
        delete it.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We'll update this page when our practices change and change the "Last updated" date above. For
        significant changes, we'll also tell you in the app or by email before they take effect.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions, requests or complaints about privacy — including for the person responsible for the
        protection of personal information at Wayplume — go to{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
      </p>
    </main>
  </div>
);

export default PrivacyPolicy;
